import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Upload, FileText, X, Loader2, CheckCircle2, AlertTriangle, AlertCircle, Trash2 } from 'lucide-react';
import { parseVCard } from '../utils/vcardParser';
import { supabase } from '../supabaseClient';
import { getCompanyDisplayName } from '../utils/companyDisplayName';
import { lastDigits } from '../utils/phoneUtils';

/**
 * Fase 2 C5 (Contatos) — Import VCF.
 *
 * Fluxo:
 *   1. Upload (.vcf, .vcard) — FileReader UTF-8
 *   2. Parse — parseVCard
 *   3. Match empresa por ORG (ILIKE em company_name/short_name/empresa)
 *   4. Status por item: 🟢 pronto · 🟡 empresa pendente · 🔴 dup possível · ❌ erro
 *   5. Salvar selecionados em loop SEQUENCIAL via RPC
 *   6. Erros não abortam — relatório final ao fim
 *
 * Não fecha vínculos antigos. Não substitui campos existentes (D6).
 */

const PAPEL_TO_TIPO = {
  buyer: 'incorporadora',
  landowner: 'proprietario',
  service_provider: 'consultor',
  internal_aston: 'incorporadora',
};
const deriveTipoContato = (papel) => PAPEL_TO_TIPO[papel] || 'incorporadora';

const StatusIcon = ({ status }) => {
  if (status === 'ready') return <span title="Pronto">🟢</span>;
  if (status === 'pending_company') return <span title="Empresa pendente">🟡</span>;
  if (status === 'duplicate') return <span title="Possível duplicata">🔴</span>;
  if (status === 'error') return <span title="Erro">❌</span>;
  if (status === 'imported') return <span title="Importado"><CheckCircle2 size={12} className="inline text-emerald-600" /></span>;
  if (status === 'linked') return <span title="Vinculado"><CheckCircle2 size={12} className="inline text-blue-600" /></span>;
  return <span>·</span>;
};

const VCardImportModal = ({ onClose, onAfterImport }) => {
  const [items, setItems] = useState([]); // [{ key, parsed, empresaCandidate, dupCandidate, selected, status, error }]
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape' && !importing) onClose?.(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose, importing]);

  // ── Match empresa para um parsed ───────────────────────────────────────
  const matchEmpresa = useCallback(async (org) => {
    if (!org || org.trim().length < 2) return null;
    const q = org.trim();
    const { data } = await supabase
      .from('empresas')
      .select('id, company_name, short_name, empresa, papel_negocio')
      .or(`company_name.ilike.%${q}%,short_name.ilike.%${q}%,empresa.ilike.%${q}%`)
      .order('company_name')
      .limit(5);
    if (!data || data.length === 0) return null;
    // Match exato UPPERCASE
    const upQ = q.toUpperCase();
    const exact = data.find((e) => (e.company_name || '').toUpperCase() === upQ
      || (e.short_name || '').toUpperCase() === upQ
      || (e.empresa || '').toUpperCase() === upQ);
    return exact || data[0];
  }, []);

  // ── Dup detection leve por email/mobile ─────────────────────────────────
  const detectDup = useCallback(async (parsed, empresaId) => {
    const found = new Map();
    if (parsed.email) {
      const { data } = await supabase
        .from('contatos')
        .select('id, first_name, last_name, email, mobile, empresa_id')
        .ilike('email', parsed.email)
        .limit(3);
      for (const c of (data || [])) found.set(c.id, { ...c, match_type: 'email' });
    }
    const tail = lastDigits(parsed.mobile, 8);
    if (tail.length >= 8) {
      const { data } = await supabase
        .from('contatos')
        .select('id, first_name, last_name, email, mobile, empresa_id')
        .ilike('mobile', `%${tail}%`)
        .limit(3);
      for (const c of (data || [])) if (!found.has(c.id)) found.set(c.id, { ...c, match_type: 'mobile' });
    }
    const arr = Array.from(found.values());
    if (arr.length === 0 || !empresaId) return arr;
    const ids = arr.map((c) => c.id);
    const { data: emps } = await supabase
      .from('contact_employments')
      .select('contact_id, company_id, is_current')
      .in('contact_id', ids)
      .eq('company_id', empresaId);
    for (const c of arr) {
      const emp = (emps || []).find((e) => e.contact_id === c.id);
      c.has_current_here = emp?.is_current === true;
    }
    return arr;
  }, []);

  const computeStatus = (it) => {
    if (!it.parsed.first_name && !it.parsed.last_name) return 'error';
    if (!it.empresaCandidate) return 'pending_company';
    if (it.dupCandidate && it.dupCandidate.length > 0) return 'duplicate';
    return 'ready';
  };

  // ── Upload + parse ─────────────────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file) return;
    setParsing(true);
    setReport(null);
    try {
      const text = await file.text();
      const parsedAll = parseVCard(text);
      if (parsedAll.length === 0) {
        setItems([]);
        setReport({ kind: 'error', msg: 'Nenhum vCard reconhecido no arquivo.' });
        return;
      }
      // Match empresa + dup em paralelo (com Promise.all). Loop pequeno (<200).
      const enriched = await Promise.all(parsedAll.map(async (parsed, idx) => {
        const empresaCandidate = await matchEmpresa(parsed.organization);
        const dupCandidate = await detectDup(parsed, empresaCandidate?.id || null);
        const it = {
          key: `vcf-${idx}`,
          parsed,
          empresaCandidate,
          dupCandidate,
          selected: true,
          status: 'ready',
          error: null,
        };
        it.status = computeStatus(it);
        if (it.status === 'error') it.selected = false;
        return it;
      }));
      setItems(enriched);
    } catch (err) {
      setReport({ kind: 'error', msg: err.message || String(err) });
    } finally {
      setParsing(false);
    }
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    handleFile(file);
  };

  // ── Import sequencial ──────────────────────────────────────────────────
  const handleImport = async () => {
    setImporting(true);
    setReport(null);
    let imported = 0;
    let linked = 0;
    let errors = 0;
    const next = [...items];

    for (let i = 0; i < next.length; i += 1) {
      const it = next[i];
      if (!it.selected || it.status === 'error' || it.status === 'imported' || it.status === 'linked') continue;
      if (!it.empresaCandidate?.id) {
        // Skip silenciosamente — usuário precisa selecionar empresa antes.
        continue;
      }
      try {
        const tipo = deriveTipoContato(it.empresaCandidate.papel_negocio);
        // eslint-disable-next-line no-await-in-loop
        const { data, error } = await supabase.rpc('rpc_create_contact_with_employment', {
          p_first_name: it.parsed.first_name || '',
          p_last_name: it.parsed.last_name || null,
          p_email: it.parsed.email || null,
          p_mobile: it.parsed.mobile || null,
          p_phone: it.parsed.phone || null,
          p_title: it.parsed.title || null,
          p_empresa_id: it.empresaCandidate.id,
          p_tipo_contato: tipo,
          p_contact_nature: 'person',
          p_source: 'vcard_import',
        });
        if (error) throw error;
        next[i] = { ...it, status: 'imported', error: null, _result: data };
        imported += 1;
      } catch (err) {
        next[i] = { ...it, status: 'error', error: err.message || String(err) };
        errors += 1;
      }
      setItems([...next]);
    }
    setImporting(false);
    setReport({
      kind: 'done',
      imported,
      linked,
      errors,
      total: next.filter((x) => x.selected && x.status !== 'pending_company').length,
    });
    onAfterImport?.();
  };

  const handleLinkExisting = async (key, contactId) => {
    const idx = items.findIndex((x) => x.key === key);
    if (idx < 0) return;
    const it = items[idx];
    if (!it.empresaCandidate?.id) return;
    setImporting(true);
    try {
      const { data, error } = await supabase.rpc('rpc_link_existing_contact_to_company', {
        p_contact_id: contactId,
        p_empresa_id: it.empresaCandidate.id,
        p_title: it.parsed.title || null,
        p_source: 'vcard_import',
      });
      if (error) throw error;
      const next = [...items];
      next[idx] = { ...it, status: 'linked', error: null, _result: data };
      setItems(next);
    } catch (err) {
      const next = [...items];
      next[idx] = { ...it, status: 'error', error: err.message || String(err) };
      setItems(next);
    } finally {
      setImporting(false);
    }
  };

  const toggleSelected = (key) => {
    setItems((prev) => prev.map((x) => x.key === key ? { ...x, selected: !x.selected } : x));
  };

  const removeItem = (key) => {
    setItems((prev) => prev.filter((x) => x.key !== key));
  };

  const counts = useMemo(() => {
    const c = { ready: 0, pending: 0, dup: 0, error: 0, done: 0 };
    items.forEach((it) => {
      if (it.status === 'ready') c.ready += 1;
      else if (it.status === 'pending_company') c.pending += 1;
      else if (it.status === 'duplicate') c.dup += 1;
      else if (it.status === 'error') c.error += 1;
      else if (it.status === 'imported' || it.status === 'linked') c.done += 1;
    });
    return c;
  }, [items]);

  const canImport = items.some((it) => it.selected && (it.status === 'ready' || it.status === 'duplicate') && it.empresaCandidate?.id);

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Importar vCard"
      onClick={(e) => { if (e.target === e.currentTarget && !importing) onClose?.(); }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl overflow-hidden flex flex-col" style={{ maxHeight: '85vh' }}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-indigo-600" />
            <h3 className="text-sm font-semibold text-slate-800">Importar vCard (.vcf)</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={importing}
            aria-label="Fechar"
            className="p-1 rounded hover:bg-slate-200 text-slate-500 disabled:opacity-50"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          {items.length === 0 && (
            <label className="block border-2 border-dashed border-slate-300 rounded p-8 text-center text-slate-500 cursor-pointer hover:bg-slate-50">
              <Upload size={20} className="mx-auto mb-1 text-slate-400" />
              <div className="text-xs">Selecione um arquivo .vcf ou .vcard</div>
              <div className="text-[10px] text-slate-400 mt-1">Suporta múltiplos contatos no mesmo arquivo (UTF-8)</div>
              <input type="file" accept=".vcf,.vcard,text/vcard" onChange={onPickFile} className="hidden" />
              {parsing && <div className="mt-2 text-[11px] text-slate-500 flex items-center justify-center gap-1"><Loader2 size={12} className="animate-spin" /> Lendo…</div>}
            </label>
          )}

          {items.length > 0 && (
            <>
              <div className="flex items-center gap-2 text-[11px] text-slate-600 mb-3 flex-wrap">
                <span>{items.length} contato(s) reconhecidos:</span>
                {counts.ready > 0 && <span className="text-emerald-700">🟢 {counts.ready} prontos</span>}
                {counts.pending > 0 && <span className="text-amber-700">🟡 {counts.pending} sem empresa</span>}
                {counts.dup > 0 && <span className="text-red-700">🔴 {counts.dup} possível dup</span>}
                {counts.error > 0 && <span className="text-red-700">❌ {counts.error} erro</span>}
                {counts.done > 0 && <span className="text-emerald-700">✓ {counts.done} ok</span>}
              </div>

              <ul className="space-y-2">
                {items.map((it) => {
                  const cName = `${it.parsed.first_name || ''} ${it.parsed.last_name || ''}`.trim() || '(sem nome)';
                  const empDisp = it.empresaCandidate ? getCompanyDisplayName(it.empresaCandidate) : null;
                  return (
                    <li
                      key={it.key}
                      className={`border rounded p-2 ${
                        it.status === 'imported' || it.status === 'linked' ? 'bg-emerald-50 border-emerald-200'
                        : it.status === 'error' ? 'bg-red-50 border-red-200'
                        : it.status === 'duplicate' ? 'bg-red-50/40 border-red-200'
                        : it.status === 'pending_company' ? 'bg-amber-50 border-amber-200'
                        : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex items-start gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={it.selected}
                          onChange={() => toggleSelected(it.key)}
                          disabled={importing || it.status === 'imported' || it.status === 'linked' || it.status === 'error' || it.status === 'pending_company'}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <StatusIcon status={it.status} />
                            <span className="font-medium text-slate-800 truncate">{cName}</span>
                            {it.parsed.title && <span className="text-[11px] text-slate-500">· {it.parsed.title}</span>}
                            {empDisp && <span className="text-[11px] text-emerald-700">· {empDisp}</span>}
                            {!empDisp && it.parsed.organization && (
                              <span className="text-[11px] text-amber-700">· ORG: "{it.parsed.organization}" sem match</span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {it.parsed.email}
                            {it.parsed.email && (it.parsed.mobile || it.parsed.phone) && ' · '}
                            {it.parsed.mobile || it.parsed.phone}
                          </div>
                          {it.status === 'duplicate' && it.dupCandidate?.length > 0 && (
                            <div className="mt-1.5 bg-white border border-red-200 rounded p-1.5 space-y-1">
                              <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold">
                                Possível duplicata
                              </div>
                              {it.dupCandidate.map((d) => {
                                const dName = `${d.first_name || ''} ${d.last_name || ''}`.trim() || '(sem nome)';
                                return (
                                  <div key={d.id} className="flex items-center gap-2 text-[11px]">
                                    <span className="flex-1 truncate">{dName} {d.email ? `· ${d.email}` : ''}</span>
                                    {d.has_current_here ? (
                                      <span className="text-red-600 text-[10px]">já vinculado a esta empresa</span>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => handleLinkExisting(it.key, d.id)}
                                        disabled={importing}
                                        className="px-1.5 py-0.5 bg-emerald-600 text-white text-[10px] rounded hover:bg-emerald-700 disabled:opacity-50"
                                      >
                                        Vincular
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {it.error && (
                            <div className="text-[11px] text-red-700 mt-1 flex items-center gap-1">
                              <AlertCircle size={10} /> {it.error}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(it.key)}
                          disabled={importing}
                          className="p-1 text-slate-400 hover:text-red-600 disabled:opacity-50"
                          title="Remover da lista"
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {report && (
            <div className={`mt-3 p-2 rounded text-[12px] ${
              report.kind === 'error' ? 'bg-red-50 border border-red-200 text-red-800'
              : 'bg-emerald-50 border border-emerald-200 text-emerald-900'
            }`}>
              {report.kind === 'error' ? (
                <span className="flex items-center gap-1"><AlertTriangle size={12} /> {report.msg}</span>
              ) : (
                <span>
                  Importação concluída: {report.imported} criado(s)
                  {report.linked > 0 && ` · ${report.linked} vinculado(s)`}
                  {report.errors > 0 && ` · ${report.errors} erro(s)`}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={importing}
            className="px-3 py-1.5 text-[12px] bg-white border border-slate-300 text-slate-700 rounded hover:bg-slate-50 disabled:opacity-50"
          >
            Fechar
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={importing || !canImport}
            className="px-3 py-1.5 text-[12px] bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
          >
            {importing && <Loader2 size={12} className="animate-spin" />}
            {importing ? 'Importando…' : 'Importar selecionados'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VCardImportModal;
